import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { findIndex, omit } from 'underscore';
import { Controller, useFormContext } from 'react-hook-form';

import t from 'react-translate';

import { BasicEnrollment, Course } from 'redux/schemas/models/course';
import { AnalyticsPageType } from 'redux/schemas/app/analytics';
import { useAppDispatch } from 'redux/store';
import { fetchAccessibleCourses } from 'redux/actions/analytics';
import { getCourseOptions } from 'redux/selectors/analytics';

import { gray2 } from 'styles/global_defaults/colors';
import { standardSpacing, quarterSpacing, doubleSpacing } from 'styles/global_defaults/scaffolding';

import NvDropdown, { NvDropdownButtonStyle, NvDropdownAlign, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import Icon from 'cohort_management/components/icon';
import { AnalyticsContext } from '../analytics-dashboard';
import { config } from '../../../../config/pendo.config.json';

export const styles = css`
  height: ${doubleSpacing}px;
  width: 400px;
  border-radius: ${quarterSpacing}px;

  input {
    width: 100%;
    height: ${standardSpacing}px;
    border: none;
    outline: none;
    font-weight: 400;
    padding: 0;
    background: none;

    ::placeholder {
      color: ${gray2};
    }
  }
`;

const dropdownItemStyles = css`
  .bs4-dropdown-item {
    max-width: 340px;
    .course-name {
      max-width: 200px;
    }
  }
`;
interface CourseSearchDropdownProps {
  onSelect: (course: Course | BasicEnrollment) => void;
}

const CourseSearchDropdown: React.FC<CourseSearchDropdownProps> = ({
  onSelect,
}) => {
  const { analyticsPageType, catalogId, institutionId } = useContext(AnalyticsContext);
  const { setValue, watch } = useFormContext();

  const [showList, setShowList] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredItems, setFilteredItems] = useState<NvDropdownOption[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const courseOptions = useSelector(state => getCourseOptions(state, analyticsPageType));

  const dispatch = useAppDispatch();
  const postCourseCatalogId = watch('postCourseCatalogId');

  const onSelectItem = useCallback((course) => {
    setInputValue(`${course.name} ${course.catalogId}`);
    setShowList(false);
    setValue('postCourseCatalogId', course.catalogId);
    onSelect?.(course);
  }, [setValue, onSelect]);


  // CustomItem accessibility not handled by NvDropdown, so we add keyboard navigation here
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowList(false);
      inputRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => {
        const nextIndex = prev + 1;
        return nextIndex < itemRefs.current.length ? nextIndex : prev;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => {
        if (prev > 0) {
          return prev - 1;
        }
        if (prev === 0) {
          inputRef.current?.focus();
          return -1;
        }
        return prev;
      });
    }
  }, []);

  const getFilteredItems = useCallback(() => {
    let filtered = [...courseOptions];

    if (inputValue?.length > 0) {
      filtered = filtered.filter(item => item.name.toLowerCase().includes(inputValue.toLowerCase())
        || item.catalogId.toLowerCase().includes(inputValue.toLowerCase()));
    }

    const dropdownItems: NvDropdownOption[] = filtered.map((item, index) => ({
      type: 'custom',
      customItem: (
        <ClickableContainer
          ref={(el) => { itemRefs.current[index] = el; }}
          className='d-flex align-items-center pl-2 pr-4 justify-content-between'
          onClick={() => onSelectItem(item)}
          onKeyDown={handleKeyDown}
          css={dropdownItemStyles}
        >
          <div className='d-flex bs4-dropdown-item mr-2 px-0 align-items-center'>
            <Icon
              course={item as Course}
              className='cohort-management-icon'
            />
            <div className='course-name text-regular px-2 ellipsis'>
              {item?.name}
            </div>
            <span className='label text-gray-2 ellipsis'>
              {item.catalogId}
            </span>
          </div>
          {postCourseCatalogId === item.catalogId && (
            <NvIcon
              icon='check'
              size='smallest'
              className='warning'
            />
          )}
        </ClickableContainer>
      ),
      id: item.catalogId,
    }));

    // Add header
    if (!dropdownItems.length) {
      dropdownItems.unshift({
        type: 'header',
        title: t.SEARCH.NO_RESULTS_FOUND(),
        id: 0,
      });
    }

    return dropdownItems;
  }, [courseOptions, inputValue, onSelectItem, postCourseCatalogId, handleKeyDown]);

  useEffect(() => {
    setShowList(inputValue?.length > 1);
    if (inputValue?.length > 1) {
      setFilteredItems(getFilteredItems());
    }
  }, [getFilteredItems, inputValue]);

  useEffect(() => {
    if (!inputValue?.length && showList) {
      setFilteredItems(getFilteredItems());
    }
  }, [getFilteredItems, inputValue?.length, showList]);

  useEffect(() => {
    if (analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS) {
      dispatch(fetchAccessibleCourses({ institutionId, catalogId }));
    }
  }, [analyticsPageType, catalogId, dispatch, institutionId]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (!showList) {
      setFocusedIndex(-1);
    }
  }, [showList]);

  const changeHandler = (e) => setInputValue(e.target.value);

  return (
    <Controller
      name='postCourseCatalogId'
      render={({ field }) => (
        <NvDropdown
          buttonStyle={NvDropdownButtonStyle.CUSTOM}
          minWidth={400}
          maxWidth={400}
          customTarget={() => (
            <div
              className='d-flex align-items-center justify-content-between border border-primary p-2'
              css={styles}
            >
              <NvTextInput
                ref={inputRef}
                value={inputValue}
                onChange={changeHandler}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && showList) {
                    e.preventDefault();
                    setFocusedIndex(0);
                  }
                }}
                className='input w-100'
                placeholder={t.COURSE_ANALYTICS.PRE_POST.MODAL.SEARCH_PLACEHOLDER()}
              />
              <NvIcon
                icon='dropdown-arrow'
                size='xss-smallest'
                onClick={() => setShowList(show => !show)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && showList) {
                    e.preventDefault();
                    setFocusedIndex(0);
                  }
                }}
                className='gray-3'
              />
            </div>
          )}
          align={NvDropdownAlign.LEFT}
          items={filteredItems}
          show={showList}
          onToggle={setShowList}
          initialIndex={findIndex(courseOptions, item => item.catalogId === postCourseCatalogId)}
          toggleDataQa={config.pendo.analyticsDashboard.prePost.setupModal.postCourseCatalogId}
          {...omit(field, ['ref'])}
        />
      )}
    />
  );
};

export default CourseSearchDropdown;
